
import {
  computed, defineComponent, onMounted, onUnmounted, ref,
} from 'vue';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';

dayjs.extend(customParseFormat);

export default defineComponent({
  name: 'SelecionarData',
  props: {
    dataSelecionada: {
      type: String,
      default: '',
    },
    horaMinuto: {
      type: Boolean,
      default: false,
    },
    horaMinutoSegundo: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    classCss: {
      type: String,
      default: '',
    },
  },
  emits: ['update:dataSelecionada', 'update:horaMinutoSegundo', 'change', 'blur', 'keydown'],
  setup(props, { emit }) {
    const datePickerRef = ref(null);
    let inputElement: HTMLInputElement | null = null;

    function obterFormatoData() {
      if (props.horaMinutoSegundo) {
        return UtilitarioData.formatoDataDiaMesAnoHoraMinutoSegundo;
      } if (props.horaMinuto) {
        return UtilitarioData.formatoDataDiaMesAnoHoraMinuto;
      }
      return UtilitarioData.formatoDataDiaMesAno;
    }

    function obterData() {
      if (UtilitarioGeral.valorValido(props.dataSelecionada)) {
        if (UtilitarioData.obterAnoData(props.dataSelecionada) === 1901) {
          return undefined;
        }
        if (props.horaMinuto) {
          return UtilitarioData.converterDataHoraMinutoParaDayJs(props.dataSelecionada);
        } if (props.horaMinutoSegundo) {
          return UtilitarioData.converterDataHoraMinutoSegundoParaDayJs(props.dataSelecionada);
        }
        return UtilitarioData.converterDataParaDayJs(props.dataSelecionada);
      }

      return undefined;
    }

    const computedDataSelecionada = computed({
      get: () => obterData(),
      set: (valor: any) => {
        if (valor === undefined || valor === null) {
          emit('update:dataSelecionada', '');
        } else if (props.horaMinuto || props.horaMinutoSegundo) {
          emit('update:dataSelecionada', UtilitarioData.converterDataHoraMinutoSegundoJson(valor));
        } else {
          emit('update:dataSelecionada', UtilitarioData.converterDataJson(valor));
        }
      },
    });

    function change() {
      emit('change', props.dataSelecionada);
    }

    function formatarDataInteligente(event: Event) {
      const valorInput = (event.target as HTMLInputElement).value;
      const soNumeros = valorInput.replace(/\D/g, '');

      if (!soNumeros || ![4, 6, 8].includes(soNumeros.length)) {
        return;
      }

      let novaData;

      if (soNumeros.length === 4) { // Formato DDMM, ano corrente
        novaData = dayjs(soNumeros, 'DDMM');
      } else if (soNumeros.length === 6) { // Formato DDMMYY
        novaData = dayjs(soNumeros, 'DDMMYY');
      } else { // soNumeros.length === 8, formato DDMMYYYY
        novaData = dayjs(soNumeros, 'DDMMYYYY');
      }

      if (novaData && novaData.isValid()) {
        computedDataSelecionada.value = novaData;
        if (datePickerRef.value) {
          // eslint-disable-next-line
          (datePickerRef.value as any).blur();
        }
      }
    }

    function handleInput(event: Event) {
      const valorInput = (event.target as HTMLInputElement).value;
      if (valorInput === '') {
        computedDataSelecionada.value = null;
      }
    }

    function handleBlur(event: FocusEvent) {
      formatarDataInteligente(event);
      emit('blur');
    }

    function handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        formatarDataInteligente(event);
      }
      emit('keydown', event);
    }

    onMounted(() => {
      if (datePickerRef.value) {
        // eslint-disable-next-line
        inputElement = (datePickerRef.value as any).$el.querySelector('input');
        if (inputElement) {
          inputElement.addEventListener('input', handleInput);
          inputElement.addEventListener('blur', handleBlur);
          inputElement.addEventListener('keydown', handleKeydown);
        }
      }
    });

    onUnmounted(() => {
      if (inputElement) {
        inputElement.removeEventListener('input', handleInput);
        inputElement.removeEventListener('blur', handleBlur);
        inputElement.removeEventListener('keydown', handleKeydown);
      }
    });

    return {
      props,
      datePickerRef,
      obterFormatoData,
      computedDataSelecionada,
      change,
    };
  },
});
